import { useMemo, memo, useCallback } from 'react';
import { cn } from '@divlab/divanui';

import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import useTranslation from '@Queries/useTranslation';
import LinksList from '../LinksList';
import styles from './ProductOptions.module.css';

import type { FC, HTMLAttributes } from 'react';

export interface ProductOptionsProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const ProductOptions: FC<ProductOptionsProps> = (props) => {
  const countOfChangedOptions = PageProductStore.getCountOfChangedParameters();
  const changeableParameters = PageProductStore.useChangeableParameters();
  const { t } = useTranslation();

  const optionSpelling = t('plural-options', countOfChangedOptions);
  const selectedSpelling = t('plural-choose', countOfChangedOptions);

  const label = useMemo(() => {
    if (changeableParameters.length === 1) {
      const firstOption = changeableParameters[0];
      const selectedVariant = firstOption.variants.find((item) => item.selected);
      const name = selectedVariant ? selectedVariant.name : t('options.choose');

      return { title: `${firstOption.name}:`, name, selected: !!selectedVariant };
    }

    if (changeableParameters.length > 1 && countOfChangedOptions === 0) {
      return {
        title: `${t('options.title')}:`,
        name: t('options.choose'),
        selected: false,
      };
    }

    if (changeableParameters.length > 1 && countOfChangedOptions > 0) {
      return {
        title: `${t('options.title')}:`,
        name: `${selectedSpelling} ${countOfChangedOptions} ${optionSpelling}`,
        selected: true,
      };
    }

    return null;
  }, [changeableParameters, countOfChangedOptions, selectedSpelling, optionSpelling, t]);

  const handleClickOptions = useCallback(() => {
    ModalWindows.open('ProductOptions', { kitIndex: null, isKit: false });
  }, []);

  if (!label) return null;

  return (
    <LinksList
      {...props}
      items={[
        {
          hasArrow: true,
          label: (
            <div className={styles.labelWrapper}>
              <div className={styles.title}>{label.title}</div>
              <div className={cn(styles.value, { [styles.selected]: label.selected })}>
                {label.name}
              </div>
            </div>
          ),
          testid: 'options',
          onClick: handleClickOptions,
        },
      ]}
    />
  );
};

export default memo(ProductOptions);
